import { useState, useRef, useEffect } from "react";
import "./UserButton.css";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Snackbar, Alert } from "@mui/material";
import {
  isUserSubscribed,
  continueSubscription,
  cancelSubscription,
  upgradeToPremium,
  changePlan,
  getUserDetails,
  fetchEnterpriseById,
} from "../../Utils/functions";
import CancelConfirmationCard from "../CancelConfirmationCard/CancelConfirmationCard";
import PlanSelectionModal from "../PlanSelectionModal/PlanSelectionModal";
import EnterpriseContactModal from "../EnterpriseContactModal/EnterpriseContactModal";

const UserButton = ({ profileMenu }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isDropDownOpen, setDropDownOpen] = useState(false);
  const [subscriptionState, setSubscriptionState] = useState("");
  const [currentPlan, setCurrentPlan] = useState(null);
  const [hasEnterprise, setHasEnterprise] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const updateSubscriptionState = async () => {
    try {
      const data = await isUserSubscribed();
      setCurrentPlan(data.currentPlan || null);
      if (data.isActive && data.cancelAtPeriodEnd) {
        setSubscriptionState("Resume Subscription");
      } else if (data.isActive && !data.cancelAtPeriodEnd) {
        setSubscriptionState("Cancel Subscription");
      } else {
        setSubscriptionState("Upgrade Plan");
      }
    } catch (error) {
      console.error("Error updating subscription state:", error);
    }
  };

  useEffect(() => {
    checkIsEnterpriseState();
    const fetchSubscriptionData = async () => {
      try {
        await updateSubscriptionState();
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setSubscriptionState("Upgrade Plan"); // Fallback
      }
    };

    fetchSubscriptionData();
  }, []);

  const handleSubscriptionClick = async () => {
    try {
      if (subscriptionState === "Resume Subscription") {
        const result = await continueSubscription();
        if (result.success) {
          setSnackbar({
            open: true,
            message: result.message,
            severity: "success",
          });

          // Refresh subscription status
          await updateSubscriptionState();
        } else {
          setSnackbar({
            open: false,
            message: result.message || "Failed to continue subscription",
            severity: "error",
          });
        }
      } else if (subscriptionState === "Cancel Subscription") {
        setShowCancelConfirmation(true);
      } else if (subscriptionState === "Upgrade Plan") {
        setShowEnterpriseModal(true);
      }
    } catch (error) {
      console.error("Error handling subscription action:", error);
      setSnackbar({
        open: true,
        message: "An error occurred. Please try again.",
        severity: "error",
      });
    }
  };

  const checkIsEnterpriseState = async () => {
    try {
      const userDetails = await getUserDetails();
      if (userDetails?.user?.Enterprise) {
        setHasEnterprise(true);
      }
    } catch (error) {
      console.error("Error checking enterprise user:", error);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelConfirmation(false);
    try {
      const result = await cancelSubscription();
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: "success",
        });

        // Refresh subscription status
        await updateSubscriptionState();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to cancel subscription",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      setSnackbar({
        open: true,
        message: "An error occurred. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCancelConfirmation = () => {
    setShowCancelConfirmation(false);
  };

  const handlePlanChange = async (planType) => {
    try {
      const result = await changePlan(planType);
      setSnackbar({ open: true, message: result.message, severity: "success" });
      await updateSubscriptionState();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Failed to change plan", severity: "error" });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <ul className={profileMenu}>
        <li className="questions-list">
          <button className="question" onClick={() => navigate("/settings")}>Settings</button>
          {(subscriptionState !== "Upgrade Plan" || hasEnterprise) && (
            <button
              className="question"
              onClick={() => navigate("/organization-dataset")}
            >
              Dataset
            </button>
          )}
          {subscriptionState && !hasEnterprise && (
            <button className="question" onClick={handleSubscriptionClick}>
              {subscriptionState}
            </button>
          )}
          {currentPlan && !hasEnterprise && (
            <button className="question" onClick={() => setShowPlanModal(true)}>
              Change Plan
            </button>
          )}
        </li>
      </ul>

      {showPlanModal && (
        <PlanSelectionModal
          onClose={() => setShowPlanModal(false)}
          currentPlan={currentPlan}
          onChangePlan={currentPlan ? handlePlanChange : null}
        />
      )}
      {showEnterpriseModal && (
        <EnterpriseContactModal onClose={() => setShowEnterpriseModal(false)} />
      )}
      {showCancelConfirmation && (
        <CancelConfirmationCard
          isVisible={showCancelConfirmation}
          onConfirm={handleConfirmCancel}
          onCancel={handleCancelConfirmation}
        />
      )}
      <Snackbar
        open={Boolean(snackbar.open)}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserButton;
